import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ShieldCheck,
  Shield,
  Wrench,
  UserRound,
  UsersRound,
  Check,
  Minus,
  RefreshCw,
  Info,
  Trash2,
  RotateCcw,
} from "lucide-react";
import PageHeader from "../../../components/shared/PageHeader";
import { getAccessOverview } from "../../../api/access";
import { updateAgent, archiveAgent, restoreAgent } from "../../../api/agent";
import { hasSuperAdminAccess } from "../../../utils/roleAccess";

// Access levels a super admin can assign from this page. `super_admin` is
// deliberately absent: the backend role field does not accept it (promoting a
// principal stays a dev/command action), so super_admin rows are shown
// read-only below rather than offered as an option.
const ASSIGNABLE_ROLES = ["agent", "admin", "dev", "pa"];

// -----------------------------------------------------------------------------
// What each access level entails.
//
// This is the human-readable mirror of the canonical role vocabulary in the
// backend `core/permissions.py`. When the backend role groups change, update
// this narrative and the matrix below to match - they are documentation, not a
// second source of truth.
// -----------------------------------------------------------------------------

const ROLE_META = {
  super_admin: {
    label: "Super Admin",
    icon: ShieldCheck,
    tint: "text-[#55C5D0]",
    chip: "bg-cyan-50 text-cyan-700 border-cyan-200",
    who: "The agency principal.",
    summary:
      "Everything an Admin can do, PLUS the two agency-level analytics environments (Agent Analytics + Executive) and agency-wide data by default - every agent's pipeline, sent contracts and dashboard tiles, without having to ask.",
  },
  admin: {
    label: "Admin",
    icon: Shield,
    tint: "text-indigo-500",
    chip: "bg-indigo-50 text-indigo-700 border-indigo-200",
    who: "Office manager / operations.",
    summary:
      "Passes every admin-only gate: Agent Management, the Settings admin panel, the Trash pages and Commissions. Does NOT see the agency-level analytics or the whole office's data by default - a plain admin only reaches another agent's pipeline by explicitly asking for it.",
  },
  dev: {
    label: "Media / Tech (Dev)",
    icon: Wrench,
    tint: "text-slate-500",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    who: "Internal media & tech team.",
    summary:
      "Feature-wise treated like an Admin and an Agent combined, so the team can build and test every page in the CRM. Deliberately NEVER counted as a sales agent - dev accounts are excluded from the leaderboard, GCI and rankings.",
  },
  agent: {
    label: "Agent",
    icon: UserRound,
    tint: "text-emerald-500",
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    who: "Sales agent.",
    summary:
      "Full agent-level access: every page, action and endpoint a sales agent needs, scoped to their own pipeline and data. Counted in all sales performance reporting.",
  },
  pa: {
    label: "Personal Assistant",
    icon: UsersRound,
    tint: "text-amber-500",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    who: "Works on an agent's behalf.",
    summary:
      "Full agent-level access - being a PA no longer removes any permission an agent has (changed 7 Sep 2026). On top of that, a PA can view the whole office on the scoped surfaces (pipelines, dashboard tiles, Sent Contracts, Buyer Enquiries & Offers, the Communications Hub).",
  },
};

// Columns in the capability matrix, most to least privileged.
const MATRIX_ROLES = ["super_admin", "admin", "dev", "agent", "pa"];

// Rows: the capability tiers from core/permissions.py, and who they cover.
// `roles` lists exactly which columns get a check.
const CAPABILITIES = [
  {
    key: "agent_access",
    label: "Agent-level CRM",
    detail: "Every page, action and endpoint a sales agent can reach.",
    roles: ["super_admin", "admin", "dev", "agent", "pa"],
  },
  {
    key: "admin_access",
    label: "Admin-only pages & actions",
    detail: "Agent Management, Settings admin panel, Trash pages, Commissions.",
    roles: ["super_admin", "admin", "dev"],
  },
  {
    key: "agency_scope",
    label: "Office-wide scoped surfaces",
    detail:
      "See the whole office on pipelines, dashboard tiles, Sent Contracts, Buyer Enquiries & Offers and the Communications Hub.",
    roles: ["super_admin", "admin", "dev", "pa"],
  },
  {
    key: "super_admin_access",
    label: "Agency-level analytics + data by default",
    detail:
      "Agent Analytics and Executive environments, plus every agent's data in scope by default.",
    roles: ["super_admin", "dev"],
  },
  {
    key: "sales_reporting",
    label: "Counted in sales reporting",
    detail: "Appears on the leaderboard, GCI and rankings.",
    roles: ["super_admin", "admin", "agent", "pa"],
  },
];

const RoleCard = ({ roleKey, count }) => {
  const meta = ROLE_META[roleKey];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
          <Icon className={`w-5 h-5 ${meta.tint}`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900">{meta.label}</h3>
            {typeof count === "number" && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.chip}`}>
                {count} {count === 1 ? "user" : "users"}
              </span>
            )}
          </div>
          <p className="text-xs font-medium text-gray-400 mt-0.5">{meta.who}</p>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{meta.summary}</p>
        </div>
      </div>
    </div>
  );
};

// `embedded` renders the same content as a tab inside Agent Management (the
// merged "Agents Access & Permissions" tab, Monday 13058000755) - same body,
// without the page chrome, since the host page already has a title.
const AccessPermissionsPage = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideInactive, setHideInactive] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Only the agency principal (super admin) can change access levels or archive
  // users from here; everyone else sees the same read-only overview as before.
  const currentUser = useSelector((s) => s.auth?.user);
  const canManage = hasSuperAdminAccess(currentUser);
  const isSelf = (u) => String(u?.id) === String(currentUser?.id);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccessOverview();
      setData(res.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Could not load the access overview. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const apiError = (e, fallback) =>
    e?.response?.data?.detail || e?.response?.data?.error || fallback;

  const handleRoleChange = async (user, newRole) => {
    if (!newRole || newRole === user.role) return;
    const label = ROLE_META[newRole]?.label || newRole;
    if (!window.confirm(`Change ${user.name}'s access level to ${label}?`)) return;
    setBusyId(user.id);
    try {
      await updateAgent(user.id, { role: newRole });
      toast.success(`${user.name} is now ${label}.`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not change the access level."));
    } finally {
      setBusyId(null);
    }
  };

  const handleArchive = async (user) => {
    if (!window.confirm(
      `Archive ${user.name}? They lose CRM access immediately. Their record and history are kept and you can restore them here later.`
    )) return;
    setBusyId(user.id);
    try {
      await archiveAgent(user.id);
      toast.success(`${user.name} archived.`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not archive this user."));
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (user) => {
    setBusyId(user.id);
    try {
      await restoreAgent(user.id);
      toast.success(`${user.name} restored.`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not restore this user."));
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countByRole = useMemo(() => {
    const map = {};
    (data?.roles || []).forEach((r) => {
      map[r.role] = r.count;
    });
    return map;
  }, [data]);

  // The roster, with inactive (deactivated / disabled) staff optionally filtered
  // out. Roles left with nobody visible drop off the list entirely.
  const rosterRoles = useMemo(() => {
    return (data?.roles || [])
      .map((r) => {
        const users = hideInactive ? r.users.filter((u) => u.is_active) : r.users;
        return { ...r, users, visibleCount: users.length };
      })
      .filter((r) => r.visibleCount > 0);
  }, [data, hideInactive]);

  const visibleStaff = useMemo(
    () => rosterRoles.reduce((n, r) => n + r.visibleCount, 0),
    [rosterRoles]
  );

  const inactiveStaff = useMemo(
    () =>
      (data?.roles || []).reduce(
        (n, r) => n + r.users.filter((u) => !u.is_active).length,
        0
      ),
    [data]
  );

  const refreshButton = (
    <button
      type="button"
      onClick={load}
      disabled={loading}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
    >
      <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
      Refresh
    </button>
  );

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 max-w-6xl mx-auto"}>
      {embedded ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-gray-600 text-sm sm:text-base">
            Who holds which access level in the CRM, and what each level lets them do.
          </p>
          {refreshButton}
        </div>
      ) : (
        <PageHeader
          title="Access & Permissions"
          subtitle="Who holds which access level in the CRM, and what each level lets them do."
          action={refreshButton}
        />
      )}

      {/* Access levels explained */}
      <section className="mt-2">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Access levels
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {MATRIX_ROLES.map((rk) => (
            <RoleCard key={rk} roleKey={rk} count={countByRole[rk]} />
          ))}
        </div>
      </section>

      {/* Capability matrix */}
      <section className="mt-8">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          What each level can do
        </h2>
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left font-semibold text-gray-700 px-4 py-3 w-[40%]">
                  Capability
                </th>
                {MATRIX_ROLES.map((rk) => (
                  <th
                    key={rk}
                    className="text-center font-semibold text-gray-600 px-2 py-3 whitespace-nowrap"
                  >
                    {ROLE_META[rk].label.replace(" (Dev)", "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((cap) => (
                <tr key={cap.key} className="border-b border-gray-50 last:border-0 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{cap.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      {cap.detail}
                    </div>
                  </td>
                  {MATRIX_ROLES.map((rk) => {
                    const has = cap.roles.includes(rk);
                    return (
                      <td key={rk} className="text-center px-2 py-3">
                        {has ? (
                          <Check className="w-4 h-4 text-emerald-500 inline" />
                        ) : (
                          <Minus className="w-4 h-4 text-gray-300 inline" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="flex items-start gap-2 text-xs text-gray-400 mt-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Access is by role. Buyer- and vendor-portal accounts are not CRM staff
          and never appear here.
        </p>
      </section>

      {/* Live roster */}
      <section className="mt-8">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Who has access
          </h2>
          <div className="flex items-center gap-3">
            {data && inactiveStaff > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hideInactive}
                  onChange={(e) => setHideInactive(e.target.checked)}
                  className="rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
                />
                Hide inactive ({inactiveStaff})
              </label>
            )}
            {data && (
              <span className="text-xs text-gray-400">
                {hideInactive ? `${visibleStaff} active` : `${data.total_staff} staff users`}
              </span>
            )}
          </div>
        </div>

        {canManage && (
          <p className="text-xs text-gray-400 mb-3">
            You can change a user's access level or archive a user below. Archiving is reversible.
          </p>
        )}

        {loading && !data && (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 text-center text-sm text-gray-500">
            Loading staff...
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-5">
            {rosterRoles.map((r) => {
                const meta = ROLE_META[r.role] || {
                  label: r.label,
                  icon: UserRound,
                  tint: "text-gray-500",
                };
                const Icon = meta.icon;
                return (
                  <div
                    key={r.role}
                    className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden"
                  >
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                      <Icon className={`w-4 h-4 ${meta.tint}`} />
                      <span className="font-semibold text-gray-900">{meta.label}</span>
                      <span className="text-xs text-gray-400">({r.visibleCount})</span>
                    </div>
                    <ul className="divide-y divide-gray-50">
                      {r.users.map((u) => (
                        <li
                          key={u.id}
                          className="flex items-center justify-between gap-3 px-4 py-2.5"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {u.name}
                              </span>
                              {!u.is_active && (
                                <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{u.email}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {u.department && (
                              <span className="text-[11px] text-gray-500 capitalize">
                                {u.department}
                              </span>
                            )}
                            {(u.secondary_roles || [])
                              .filter((sr) => sr !== r.role)
                              .map((sr) => (
                                <span
                                  key={sr}
                                  className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize"
                                  title="Additional role"
                                >
                                  +{ROLE_META[sr]?.label || sr}
                                </span>
                              ))}
                            {canManage &&
                              (isSelf(u) ? (
                                <span className="text-[11px] text-gray-400 italic">You</span>
                              ) : r.role === "super_admin" ? (
                                <span
                                  className="text-[11px] text-gray-400"
                                  title="Super admins are managed by the dev team"
                                >
                                  Principal
                                </span>
                              ) : (
                                <>
                                  <select
                                    value={r.role}
                                    disabled={busyId === u.id}
                                    onChange={(e) => handleRoleChange(u, e.target.value)}
                                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0] disabled:opacity-50"
                                    title="Change access level"
                                    aria-label={`Access level for ${u.name}`}
                                  >
                                    {ASSIGNABLE_ROLES.map((role) => (
                                      <option key={role} value={role}>
                                        {ROLE_META[role]?.label || role}
                                      </option>
                                    ))}
                                  </select>
                                  {u.is_active ? (
                                    <button
                                      type="button"
                                      onClick={() => handleArchive(u)}
                                      disabled={busyId === u.id}
                                      title="Archive user"
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleRestore(u)}
                                      disabled={busyId === u.id}
                                      title="Restore user"
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AccessPermissionsPage;
